import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getClient } from 'lane-shared/apollo';
import { ContentType } from 'application-types';
import { useGetAuthToken } from 'hooks/useGetAuthToken';
import { useIsMounted } from 'hooks/useIsMounted';
import { useUserDataContext } from 'lane-shared/hooks';
import { getPrimaryEmail } from 'lane-shared/helpers/user';
import { authorizationHeader } from 'lane-shared/helpers/formatters';
import { getTenantChannelsByUser } from 'lane-shared/graphql/visitorManagement';
import { TenantType } from 'lane-shared/domains/visitorManagement/types';
import { convertToUUID } from 'uuid-encoding';
import {
  VantifyWorkOrderMain,
  Header,
  AccessDeniedCard,
} from 'integrations-vantify-web-react';
import {
  VantifyContextProvider,
  useVantifyContext,
} from 'integrations-vantify-context-providers';
import { Loading } from 'design-system-web';
import styles from './Welcome.module.scss';

type Props = {
  content: ContentType;
};

function VantifyContent({ content }: Props) {
  const getAuthToken = useGetAuthToken();
  const { setAuth, auth, setUser, setContent } = useVantifyContext();
  const isMounted = useIsMounted();
  const { user } = useUserDataContext();
  const channelId = content?.channel?._id;
  const [isSettingUpAuth, setIsSettingUpAuth] = useState(false);

  const [fetchTenantInfo, { data: tenantData, loading: tenantInfoLoading, called: tenantInfoCalled }] = useLazyQuery<{
    tenantChannelsByUser: TenantType[];
  }>(getTenantChannelsByUser, {
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const channels = tenantData?.tenantChannelsByUser;
  const tenantInfo = channels?.length === 1 ? channels[0] : undefined;

  useEffect(() => {
    if (channelId && user?._id) {
      fetchTenantInfo({
        variables: {
          userId: user._id,
          channelId,
        },
      });
    }
  }, [channelId, user?._id, fetchTenantInfo]);

  useEffect(() => {
    if (!content || !isMounted()) {
      return;
    }

    setContent(content);

    async function setupAuth() {
      const currentChannelId = auth?.channelId
        ? convertToUUID(auth.channelId)
        : '';
      const newChannelId = content.channel?._id
        ? convertToUUID(content.channel._id)
        : '';

      if (!auth || currentChannelId !== newChannelId) {
        setIsSettingUpAuth(true);

        try {
          const authTokenResult = await getAuthToken();

          if (isMounted()) {
            setAuth({
              channelId: newChannelId,
              token: authorizationHeader(authTokenResult),
            });
          }
        } catch (error) {
          console.error('Failed to setup Vantify authentication:', error);
        } finally {
          if (isMounted()) {
            setIsSettingUpAuth(false);
          }
        }
      }
    }

    setupAuth();
  }, [auth, content, getAuthToken, setAuth, setContent, isMounted]);

  useEffect(() => {
    if (user) {
      const email = user.logins ? getPrimaryEmail(user.logins) : null;
      const name = user.name || null;

      setUser({ email, name });
    }
  }, [user, setUser]);

  const isUserTenantInMappings = useMemo(() => {
    const tenantMappings = content?.integration?.settings?.tenantMappings;
    const userTenantId = tenantInfo?.id;

    if (
      !tenantMappings ||
      !Array.isArray(tenantMappings) ||
      tenantMappings.length === 0 ||
      !userTenantId
    ) {
      return false;
    }

    return tenantMappings.some(
      mapping => mapping?.vtsTenantId === userTenantId
    );
  }, [tenantInfo?.id, content?.integration?.settings?.tenantMappings]);


  const isLoading = useMemo(() => {
    return isSettingUpAuth ||
      (channelId && user?._id && (tenantInfoLoading || !tenantInfoCalled));
  }, [isSettingUpAuth, channelId, user?._id, tenantInfoLoading, tenantInfoCalled]);

  const shouldShowAccessDenied = useMemo(() => {
    if (isLoading) {
      return false;
    }

    return !auth || !content?.integration || !isUserTenantInMappings;
  }, [auth, content, isUserTenantInMappings, isLoading]);


  if (isLoading) {
    return <Loading fullscreen testId="vantify-welcome-loading" />;
  }

  if (shouldShowAccessDenied) {
    return (
      <div className={styles.container}>
        <Header disabled />
        <AccessDeniedCard />
      </div>
    );
  }

  return <VantifyWorkOrderMain />;
}

export default function Vantify({ content }: Props) {
  return (
    <VantifyContextProvider>
      <VantifyContent content={content} />
    </VantifyContextProvider>
  );
}
